import { memo, useCallback } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import "./CreditCardFieldset.scss";
import { FormikTextField } from "app/components/form/FormikTextField/FormikTextField";
import { useFormikContext } from "formik";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import loadable from "@loadable/component";

const AdyenPayment = loadable(
	() =>
		import(
			/* webpackChunkName: "adyen-payment" */ "app/pages/Booking/Payment/PaymentData/AdyenPayment/AdyenPayment"
		)
);
const AdyenFlowPayment = loadable(
	() =>
		import(
			/* webpackChunkName: "adyen-flow-payment" */ "app/pages/Booking/Payment/PaymentData/AdyenFlowPayment/AdyenFlowPayment"
		)
);

const CreditCardFieldset = ({
	showAmountToPay,
	isAdyenFlow,
	isAmountToPayRequired,
	updateEncryptedCard = () => {},
	adyenFlowPaymentSession = () => {},
	onAdyenComponentMounted = () => {},
	onSubmit = () => {},
	onAdyenAdditionalDetails = () => {},
	acceptedCreditCardTypes = [],
}) => {
	const { setFieldError } = useFormikContext();
	const { track } = useAnalytics();

	const onCreditCardDataChange = useCallback(data => {
		updateEncryptedCard(data);
		setFieldError("payment"); // supprimer les messages d'erreurs formik
	}, []);

	const handleAdyenError = useCallback(error => {
		track("adyen_error", {
			errorName: error?.name,
			errorCause: error?.cause,
		});
	}, []);

	// for data-browsing-ignore usage, see https://help.crisp.chat/en/article/how-to-prevent-elements-from-being-tracked-by-magicbrowse-ju7mcg/
	return (
		<div className="creditcard" data-browsing-ignore data-testid="credit-card-form">
			{showAmountToPay && (
				<div className="creditcard__item creditcard__item--amount">
					<FormikTextField
						type="number"
						label={
							<span>
								<FormattedMessage id="creditcard.amount.label" />
								{isAmountToPayRequired ? "*" : ""}
							</span>
						}
						data-testid="creditcard-amount-input"
						name="payment.card.amount"
						id="creditcard-amount"
					/>
				</div>
			)}
			{isAdyenFlow === true && (
				<AdyenFlowPayment
					acceptedCreditCardTypes={acceptedCreditCardTypes}
					adyenFlowPaymentSession={adyenFlowPaymentSession}
					onChange={onCreditCardDataChange}
					onSubmit={onSubmit}
					onAdyenAdditionalDetails={onAdyenAdditionalDetails}
					onAdyenComponentMounted={onAdyenComponentMounted}
					onError={handleAdyenError}
				/>
			)}

			{isAdyenFlow === false && (
				<AdyenPayment
					acceptedCreditCardTypes={acceptedCreditCardTypes}
					onChange={onCreditCardDataChange}
					onAdyenComponentMounted={onAdyenComponentMounted}
					onError={handleAdyenError}
				/>
			)}
		</div>
	);
};

CreditCardFieldset.propTypes = {
	updateEncryptedCard: PropTypes.func,
	onAdyenComponentMounted: PropTypes.func,
	onAdyenAdditionalDetails: PropTypes.func,
	onSubmit: PropTypes.func,
	acceptedCreditCardTypes: PropTypes.array,
	adyenFlowPaymentSession: PropTypes.object,
	showAmountToPay: PropTypes.bool,
	isAdyenFlow: PropTypes.bool,
	isAmountToPayRequired: PropTypes.bool,
};

export default memo(CreditCardFieldset);
